import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { Banner, formatComponentNames, getSvgByName } from '@repo/ui';
import { parseDate } from 'helpers';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useFeature from 'services/features/useFeature';
import { addClosedBanner, checkIfBannerIsClosed } from 'store/banners';

import styles from './PartialResultsBanner.module.css';

const BANNER_ID = 'partial-results-banner';
/**
 * Flag gating both banners, and carrying the "results added" cutoff.
 */
const FEATURE_FLAG = 'partial_results_banner';
const RESULTS_ADDED_FROM_VARIABLE = 'results_added_from';

/**
 * Dismissal is keyed by kit *and* by what is being announced, never by kit
 * alone: a kit announces one "results added" banner per release date, and
 * dismissing an earlier one must not suppress a later one.
 */
const dismissKey = (kitId: string, announcement: string) =>
  `${kitId}:${announcement}`;
const releaseDay = (date: string) => date.slice(0, 10);
const warnedCutoffs = new Set<string>();

/**
 * The flag's `results_added_from` cutoff, or `null` if it is unusable.
 *
 * Fails closed — an unusable cutoff hides the "results added" banner rather
 * than showing it to everyone — and says so out loud, because the flag being on
 * while nothing appears is otherwise a silent, hard-to-diagnose state.
 */
const parseCutoff = (value: unknown): string | null => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))
    return value;

  const seen = String(value);
  if (!warnedCutoffs.has(seen)) {
    warnedCutoffs.add(seen);
    console.warn(
      `[${FEATURE_FLAG}] The flag is on but its ` +
        `"${RESULTS_ADDED_FROM_VARIABLE}" variable is not a YYYY-MM-DD date ` +
        `(got: ${JSON.stringify(value)}). The "results added" banner stays ` +
        `hidden until it is fixed.`,
    );
  }
  return null;
};

const PendingComponentsBanner = ({
  pendingComponents,
}: {
  pendingComponents: string[];
}) => (
  <Banner
    title='Some test results are not available yet'
    content={
      `${formatComponentNames(pendingComponents)} results aren't available ` +
      `yet. We’ll notify you when they are added to your report.`
    }
    color='orange'
    icon={getSvgByName('alertCircleFilled')}
    iconContainerClassName={styles.pendingIconContainer}
    iconClassName={styles.pendingIcon}
    className={styles.container}
    contentClassName={styles.content}
    size='sm'
    variant='full'
  />
);

/**
 * "New test results added to your report" — shown whenever a component's
 * results have landed, whether or not others are still outstanding.
 */
const ResultsAddedBanner = ({
  kitId,
  addedComponents,
  addedOn,
}: {
  kitId: string;
  addedComponents: string[];
  addedOn: string;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Keyed by the release date, not by `results_state`: a kit whose add-ons land
  // on separate days shows this banner once per release, and dismissing the
  // first must not suppress the announcement of the second.
  const keyId = dismissKey(kitId, addedOn);
  const isClosed = useSelector(
    checkIfBannerIsClosed({ bannerId: BANNER_ID, keyId }),
  );

  return (
    <Banner
      title='New test results added to your report'
      content={
        `Your ${formatComponentNames(addedComponents)} results have been ` +
        `added to your report on ` +
        `${format(parseDate(releaseDay(addedOn)), 'MMM d, yyyy')}.`
      }
      color='purple'
      className={styles.container}
      contentClassName={styles.content}
      actionClassName={styles.addedAction}
      size='sm'
      variant='full'
      isClosed={isClosed}
      onClose={() => {
        dispatch(addClosedBanner({ bannerId: BANNER_ID, keyId }));
      }}
      action={{
        label: 'View',
        onClick: () => navigate(`/results/${kitId}/metrics/all-metrics`),
      }}
    />
  );
};

/**
 * Banners reflecting the kit's live partial/complete results state, read fresh
 * off the backend-computed `results_state` virtual on every fetch
 */
export const PartialResultsBanner = () => {
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const { isFeatureEnabled, variables } = useFeature(FEATURE_FLAG);

  if (!isFeatureEnabled) return null;

  const resultsState = currentKit?.results_state;
  if (!currentKitId || !currentKit || !resultsState) return null;
  if (resultsState === 'pending') return null;

  const pendingComponents = currentKit.pending_components ?? [];
  const releasedVariants = (currentKit.variants ?? []).filter(
    variant => variant.date_results_ready,
  );
  const latestRelease = releasedVariants.reduce<string | null>(
    (latest, variant) => {
      const date = variant.date_results_ready as string;
      if (!latest) return date;
      return releaseDay(date) > releaseDay(latest) ? date : latest;
    },
    null,
  );
  // Every component sharing that day is named, not just the first: two add-ons
  // released together read as one "your A and B results have been added".
  const latestComponents = releasedVariants
    .filter(
      variant =>
        releaseDay(variant.date_results_ready as string) ===
        releaseDay(latestRelease as string),
    )
    .map(variant => variant.sampling_type)
    .filter((samplingType): samplingType is string => !!samplingType);

  // A missing or malformed cutoff suppresses the banner rather than showing it
  // to everyone: the failure mode of a mistyped flag variable should be a
  // customer missing one announcement, not every historical customer being told
  // that results they read months ago are new.
  const cutoff = parseCutoff(variables?.[RESULTS_ADDED_FROM_VARIABLE]);

  // The banner announces results the customer was *waiting on*, so it requires
  // a component that landed after the kit itself was released
  const addedAfterRelease =
    !!latestRelease &&
    !!currentKit.date_results_ready &&
    releaseDay(latestRelease) > releaseDay(currentKit.date_results_ready);

  // Deliberately independent of `results_state`: a kit can have one add-on that
  // just landed and another still outstanding
  const showResultsAdded =
    !!latestRelease &&
    !!latestComponents.length &&
    addedAfterRelease &&
    !!cutoff &&
    releaseDay(latestRelease) >= cutoff;

  if (!pendingComponents.length && !showResultsAdded) return null;

  return (
    <>
      {pendingComponents.length ? (
        <PendingComponentsBanner pendingComponents={pendingComponents} />
      ) : null}
      {showResultsAdded ? (
        <ResultsAddedBanner
          kitId={currentKitId}
          addedComponents={latestComponents}
          addedOn={latestRelease}
        />
      ) : null}
    </>
  );
};
